import {useQuery, useQueryClient} from '@tanstack/react-query'
import L from 'leaflet'
import {BlockUI} from 'primereact/blockui'
import {Button} from 'primereact/button'
import {Dialog} from 'primereact/dialog'
import {useEventListener} from 'primereact/hooks'
import {InputNumber} from 'primereact/inputnumber'
import React, {useCallback, useEffect, useMemo, useRef} from 'react'
import {Circle, MapContainer, Marker, TileLayer} from 'react-leaflet'
import {useParams} from 'react-router-dom'
import {SystemRole} from '../../Constants'
import {ReactComponent as AddIcon} from '../../assets/icons/AddIcon.svg'
import {ReactComponent as AddedLocation} from '../../assets/icons/AddedLocation.svg'
import {ReactComponent as DuoMap} from '../../assets/icons/DuoMap.svg'
import {ReactComponent as DuoWatch} from '../../assets/icons/DuoWatch.svg'
import {ReactComponent as HomeIcon} from '../../assets/icons/HomeLocationIcon.svg'
import {ReactComponent as LocationIcon} from '../../assets/icons/LocationIcon.svg'
import {ReactComponent as NokIcon} from '../../assets/icons/NokIcon.svg'
import {ReactComponent as PatientLocationIcon} from '../../assets/icons/PatientIcon.svg'
import {ReactComponent as SetLocation} from '../../assets/icons/SetLocation.svg'
import locationHomeIcon from '../../assets/icons/locationHome.svg'
import locationPointerIcon from '../../assets/icons/locationMarker.png'
import patientMakerIcon from '../../assets/icons/patientMakerIcon.png'
import setLocationIcon from '../../assets/icons/setLocationMarker.png'
import {useAppConfig} from '../../context/app/AppContext'
import TranslateUtils from '../../i18n/TranslateUtils'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../utils/BlockScrollsUtils'
import AddressForm from '../address/AddressForm'
import {useAuth} from '../authentication/AuthProvider'
import {getClientId, getSuperAdminRole} from '../authentication/AuthUtils'
import AuditLogButton from '../generic-log/AuditLogButton'
import GenericLogTable from '../generic-log/GenericLogTable'
import {severityNotification} from '../notification/NotificationConstant'
import {showNotification} from '../notification/NotificationService'
import {useRoleContext} from '../role/RoleProvider'
import {convertKeyToLabel} from '../table-utils/TableUtils'
import {watchConfigList} from './Contants'

import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {Checkbox} from 'primereact/checkbox'
import {useUserHospital} from '../../context/hospital/UserHospitalContext'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import {
    QueryCRUDTypes,
    QueryKeyManager,
} from '../../query-manager/QueryKeysManager'
import StyledTitle from '../layout/StyledTitle'
import {permissionOptionKeys} from '../role/RoleConstant'
import {usePatientContext} from '../styled-component/information/StyleDetailInformationWrapper'
import {
    getClientGeneralConfig,
    getGeofencingLocation,
    getLocationOptions,
    getUserWatchConfig,
} from './WatchConfigEndPoint'
import WatchNetwork from './WatchNetwork'
import {
    useClientWatchConfigCRUD,
    useUserWatchConfigCRUD,
} from './query-keys/WatchConfigKeysManager'

const LocationOptionsQueryKey = 'Location-Options'

const iconStyle = {width: '35px', height: '35px'}

const WatchConfig = ({PATH = 'generalConfig', type = 'client'}) => {
    useSignals()
    //#region ID
    const {id: idString} = useParams()
    const id = useMemo(() => {
        return idString ? parseInt(idString) : null
    }, [idString])
    //#endregion

    //#region Context
    const {user} = useAuth()
    const {userRole} = useAppConfig()
    const {handleCheckPermissionByKey} = usePatientContext()
    const {myRolePermissions} = useRoleContext()

    const {currentHospital} = useUserHospital()
    //#endregion

    //#region Translate
    const {translateCommons} = TranslateUtils()
    //#endregion

    //#region Query
    const queryClient = useQueryClient()

    const queryKey = useMemo(() => {
        if (type === 'client') {
            const {clientKeys} = QueryKeyManager(QueryKeyTypes.client, user?.id)
            return clientKeys.config({id})
        } else if (type !== 'client') {
            const {watchConfigKeys} = QueryKeyManager(
                QueryKeyTypes.watchConfig,
                user?.id,
            )
            return watchConfigKeys.detailByUser({userId: id})
        }
    }, [id, type, user?.id])

    const {
        data: configuration,
        // refetch,
        // isFetched,
    } = useQuery(
        queryKey,
        () =>
            type === 'client'
                ? getClientGeneralConfig(user?.id, id, (type = 'watchConfig'))
                : getUserWatchConfig(id),

        {
            enabled:
                !!user?.id && !!id && !!getClientId() && !!queryKey && !!type,
        },
    )

    const {data: locationOptions} = useQuery(
        [LocationOptionsQueryKey, user?.id, id],
        () => getLocationOptions(currentHospital?.id, id),
        {
            enabled:
                !!user?.id &&
                !!id &&
                !!currentHospital?.id &&
                (userRole === SystemRole.ADMIN || userRole === SystemRole.HCP),
        },
    )

    const {mutateAsync: createOrUpdateClientWatchConfig} =
        useClientWatchConfigCRUD(
            !!configuration?.id ? QueryCRUDTypes.update : QueryCRUDTypes.create,
            user?.id,
        )

    const {mutateAsync: createOrUpdateUserWatchConfig} = useUserWatchConfigCRUD(
        !!configuration?.id ? QueryCRUDTypes.update : QueryCRUDTypes.create,
        user?.id,
        id,
    )
    //#endregion

    //#region State
    const visible = useSignal(false)
    const activeIndex = useSignal(0)
    const currentLocation = useSignal([0, 0])
    const center = useSignal(currentLocation.value)
    const isAllowGetLocation = useSignal(false)
    const holderCircle = useSignal([])
    //#endregion

    //#region Ref
    const genericRef = useRef()
    const mapRef = useRef(null)
    //#endregion

    //#region Handle Function
    const hideDialog = () => {
        visible.value = false
        unblockOverflownScroll()
    }

    // const {mutate: updateConfig} = useMutation(
    //     toPostData => createOrUpdateGenericInfo(PATH, user?.id, toPostData),
    //     {
    //         onSuccess: data => {
    //             if (!!data?.id) {
    //                 showNotification(
    //                     severityNotification.success,
    //                     'Update configuration successfully !!',
    //                 )

    //                 queryClient.setQueryData([QueryKey, user?.id], () =>
    //                     data?.watchConfig ? data?.watchConfig : {},
    //                 )
    //             }
    //         },
    //     },
    // )

    // const {mutate: updateUserConfig} = useMutation(
    //     toPostData => updateUserWatchConfig(user?.id, id, toPostData),
    //     {
    //         onSuccess: data => {
    //             if (!!data) {
    //                 showNotification(
    //                     severityNotification.success,
    //                     'Update configuration successfully !!',
    //                 )

    //                 queryClient.setQueryData(
    //                     [QueryKey, user?.id, id],
    //                     () => data,
    //                 )
    //             }
    //         },
    //     },
    // )

    const handleUpdateConfig = async () => {
        if (type === 'client') {
            let toPostData = {
                watchConfig: configuration ? configuration : {},
            }

            await createOrUpdateClientWatchConfig(toPostData)
        } else {
            let toPostUserData = {
                ...configuration,
            }

            if (
                Array.isArray(holderCircle.value) &&
                holderCircle.value?.length === 2
            ) {
                toPostUserData = {
                    ...toPostUserData,
                    ...configuration.location,
                    location: {
                        latitude: holderCircle.value[0],
                        longitude: holderCircle.value[1],
                        radius: radiusValue,
                    },
                }
            }

            await createOrUpdateUserWatchConfig(toPostUserData)

            holderCircle.value = []
        }

        //TODO: Temporary Refetch Table
        queryClient.invalidateQueries({
            queryKey: queryKey,
        })
    }
    //#endregion

    //#region Event Listener
    // useEffect(() => {
    //     if (!!QueryKey && !!id) {
    //         refetchEvent.on('REFETCH-DATA-TABLE-' + QueryKey + id, () => {
    //             refetch()
    //         })

    //         detailEvent.on(
    //             'DETAIL-INFORMATION-RELOAD-' + QueryKey + id,
    //             refetch,
    //         )

    //         return () => {
    //             refetchEvent.removeListener(
    //                 'REFETCH-DATA-TABLE-' + QueryKey + id,
    //             )
    //             removeDetailInformationReload(QueryKey + id)
    //         }
    //     }
    // }, [id, refetch])
    //#endregion

    //#region Memo
    const circleLocation = useMemo(() => {
        const {latitude, longitude} = configuration?.location ?? {}
        return latitude && longitude ? [latitude, longitude] : []
    }, [configuration?.location])

    const radiusValue = useMemo(() => {
        const min = 100
        return Math.max(min, configuration?.location?.radius ?? min)
    }, [configuration?.location?.radius])

    //#endregion

    //#region Effect
    useEffect(() => {
        if (!!circleLocation[0] && !!circleLocation[1]) {
            center.value = [circleLocation[0], circleLocation[1]]

            mapRef?.current?.setView([circleLocation[0], circleLocation[1]], 16)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [circleLocation])
    //#endregion

    //#region GET CURRENT LOCATION
    useEffect(() => {
        if (!getSuperAdminRole()) {
            if (
                currentLocation.value[0] !== 0 &&
                currentLocation.value[1] !== 0
            ) {
                return
            }

            const getLocation = () => {
                if (navigator.geolocation) {
                    navigator.geolocation.getCurrentPosition(
                        position => {
                            const {latitude, longitude} = position.coords
                            currentLocation.value = [latitude, longitude]
                            isAllowGetLocation.value = true
                        },
                        error => {
                            console.error('Error getting location:', error)
                        },
                        {
                            enabledHighAccuracy: true,
                        },
                    )
                } else {
                    console.error(
                        'Geolocation is not supported by this browser.',
                    )
                }
            }

            getLocation()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [center, currentLocation])
    //#endregion

    // #region GET OTHER ADDRESS FROM INPUT
    const addressRef = useRef({})
    const otherLocations = useSignal([])
    const handleGetOtherLocation = async () => {
        let data = addressRef?.current?.getValue() ?? {}

        if (!!data) {
            const geoLocation = await getGeofencingLocation(data)
            if (geoLocation?.latitude && geoLocation.longitude) {
                let formattedAddress = ''
                if (data?.addressLine1) {
                    formattedAddress = data?.addressLine1
                }

                if (data?.city) {
                    formattedAddress += ', ' + data.city
                }

                if (data?.refState) {
                    formattedAddress += ', ' + data.refState
                }

                if (data?.refCountry) {
                    formattedAddress += ', ' + data.refCountry
                }

                let otherLocationHolder = [
                    geoLocation?.latitude,
                    geoLocation?.longitude,
                ]

                otherLocations.value = [
                    ...otherLocations.value,
                    [formattedAddress, ...otherLocationHolder],
                ]

                let activeIndex = 0
                if (locationList?.length + 1 - 2 > 0) {
                    activeIndex = locationList?.length + 1 - 2
                }
                handleOnActiveIndex(activeIndex, otherLocationHolder, '')

                hideDialog()
            }
        }
    }
    //#endregion

    //#region Location Icon Properties
    const getLocationOptionIcon = useCallback(iconName => {
        switch (iconName) {
            case 'setLocation':
                return <SetLocation style={iconStyle} />
            case 'currentLocation':
                return <LocationIcon style={iconStyle} />
            case 'patientLocation':
                return <PatientLocationIcon style={iconStyle} />
            case 'addedLocation':
                return <AddedLocation style={iconStyle} />
            case 'home':
                return <HomeIcon style={iconStyle} />
            case 'nok':
                return <NokIcon style={iconStyle} />
            default:
                return <AddIcon style={iconStyle} />
        }
    }, [])

    const getIconMarker = (iconName = '') => {
        let url = ''
        switch (iconName) {
            case 'setLocation':
                url = setLocationIcon
                break
            case 'currentLocation':
                url = locationPointerIcon
                break
            case 'patientLocation':
                url = patientMakerIcon
                break
            case 'home':
                url = locationHomeIcon
                break
            case 'nok':
                url = locationPointerIcon
                break
            default:
                url = locationPointerIcon
                break
        }

        return L.icon({
            iconUrl: url,
            iconSize: iconName === 'setLocation' ? [30, 40] : [40, 40],
            iconAnchor: [20, 20],
            popupAnchor: [0, 0],
        })
    }
    //#endregion

    //#region Current Location

    //#region State
    const isMapHovered = useSignal(false)
    //#endregion

    //#region Ref
    const mapContainerRef = useRef(null)
    //#endregion

    //#region Memo
    const locationList = useMemo(() => {
        let optionsList = []

        if (!!circleLocation[0] && !!circleLocation[1]) {
            optionsList.push({
                key: 0,
                label: 'Set Location',
                icon: getLocationOptionIcon('setLocation'),
                markerIcon: () => getIconMarker('setLocation'),
                latitude: circleLocation[0],
                longitude: circleLocation[1],
                onClick: () => {},
            })
        }

        if (
            !!isAllowGetLocation.value &&
            !!currentLocation.value[0] &&
            !!currentLocation.value[1]
        ) {
            optionsList.push({
                key: optionsList.length + 1,
                label: translateCommons('Current Location'),
                icon: getLocationOptionIcon('currentLocation'),
                markerIcon: () => getIconMarker('currentLocation'),
                latitude: currentLocation.value[0],
                longitude: currentLocation.value[1],
                onClick: () => {},
            })
        }

        if (locationOptions?.length > 0) {
            const locationOptionItems = locationOptions?.map((item, index) => {
                if (!!item?.latitude && !!item?.longitude) {
                    return {
                        key: index,
                        label: item?.label,
                        icon: getLocationOptionIcon(item?.icon),
                        markerIcon: () => getIconMarker(item?.icon),
                        latitude: item?.latitude,
                        longitude: item?.longitude,
                        onClick: () => {},
                    }
                }
            })

            optionsList.push(...locationOptionItems)
        }

        if (otherLocations.value?.length > 0) {
            const otherLocationsItems = otherLocations.value?.map(
                (item, index) => {
                    if (!!item[1] && !!item[2]) {
                        return {
                            key: index,
                            label: item[0],
                            icon: getLocationOptionIcon('addedLocation'),
                            markerIcon: () => getIconMarker(),
                            latitude: item[1],
                            longitude: item[2],
                            onClick: () => {},
                        }
                    }
                },
            )

            optionsList.push(...otherLocationsItems)
        }

        optionsList.push({
            key: optionsList.length + 1,
            label: translateCommons('Other Address'),
            icon: getLocationOptionIcon('otherAddress'),
            markerIcon: () => getIconMarker('currentLocation'),
            btnType: 'otherAddress',
            onClick: () => {},
        })

        return optionsList
    }, [
        locationOptions,
        isAllowGetLocation.value,
        getLocationOptionIcon,
        translateCommons,
        currentLocation.value,
        circleLocation,
        otherLocations.value,
    ])
    //#endregion

    //#region Handle Function
    const handleOnActiveIndex = (idx, location = [], type = '') => {
        if (!!type && type === 'otherAddress') {
            visible.value = true
            blockOverflownScroll()
            return
        }
        const map = mapRef.current

        if (!!location[0] && !!location[1]) {
            center.value = [...location]
            map?.setView([...location], 16)
        }

        activeIndex.value = idx
    }

    const handleOnChangeValue = (key, value, min) => {
        if (!value) {
            showNotification(
                severityNotification.warn,
                `${convertKeyToLabel(key)} cannot be empty !`,
            )
            return
        }
        if (!!min && value < min) {
            showNotification(
                severityNotification.warn,
                `${convertKeyToLabel(key)} should be greater than ${
                    min < 10 ? '0' + min : min
                } ${key === 'radius' ? 'meter' : 'minutes'} !`,
            )
            return
        }

        let toPostData = {
            ...configuration,
            [key]: value > min ? value : min,
        }
        if (key === 'radius') {
            toPostData = {
                ...toPostData,
                location: {
                    ...toPostData.location,
                    [key]: value > min ? value : min,
                },
            }
        } else {
            if (key === 'enableGeoFencing') {
                toPostData = {...toPostData, [key]: value}
            } else {
                toPostData = {...toPostData, [key]: value > min ? value : min}
            }
        }

        queryClient.setQueryData(queryKey, () => ({
            ...toPostData,
        }))
    }

    const handleOnMakerClick = data => {
        let latlng = []
        if (data?.lat && data?.lng) {
            latlng = [data.lat, data.lng]
        }

        if (
            !!circleLocation[0] &&
            !!circleLocation[1] &&
            parseFloat(circleLocation[0]) === data?.lat &&
            parseFloat(circleLocation[1]) === data?.lng
        ) {
            holderCircle.value = []
            return
        }

        holderCircle.value = latlng
    }

    //#endregion

    //#region Event Listener
    const [bindMouseEnterListener, unbindMouseEnterListener] = useEventListener(
        {
            target: mapContainerRef,
            type: 'mouseenter',
            listener: () => {
                isMapHovered.value = true
            },
        },
    )

    const [bindMouseLeaveListener, unbindMouseLeaveListener] = useEventListener(
        {
            target: mapContainerRef,
            type: 'mouseleave',
            listener: () => {
                isMapHovered.value = false
            },
        },
    )
    //#endregion

    //#region Effect
    useEffect(() => {
        bindMouseEnterListener()
        bindMouseLeaveListener()

        return () => {
            unbindMouseEnterListener()
            unbindMouseLeaveListener()
        }
    }, [
        bindMouseEnterListener,
        bindMouseLeaveListener,
        unbindMouseEnterListener,
        unbindMouseLeaveListener,
    ])
    //#endregion
    //#endregion

    return (
        <React.Fragment>
            {/* Watch Network Config */}
            {(userRole === SystemRole.ADMIN ||
                userRole === SystemRole.USER_WITH_ROLE) && (
                <div className="flex flex-column gap-3 mb-5">
                    <hr />

                    <WatchNetwork id={id} user={user} />
                </div>
            )}

            {/* Watch Geofence Config */}
            {(userRole === SystemRole.ADMIN ||
                userRole === SystemRole.USER_WITH_ROLE) && (
                <div className="flex flex-column gap-3 mb-5">
                    <hr />

                    <div className="flex  border-round flex-wrapw-full align-items-center justify-content-between">
                        {/* <h5
                            className={`pl-2 text-black font-bold w-full m-0 py-2 text-center  md:text-left`}>
                            <span className="mr-3">
                                <MapIcon
                                    style={{width: '20px', height: '20px'}}
                                />
                            </span>
                            {translateCommons('Geofencing Configuration')}
                        </h5> */}
                        <StyledTitle
                            title={translateCommons('Geofencing Configuration')}
                            icon={<DuoMap />}
                            description={translateCommons(
                                `This configuration section is used for triggering watches alerting when the patient is out of the geofencing area`,
                            )}
                        />
                        <div className="field-checkbox px-0">
                            <Checkbox
                                checked={configuration?.enableGeoFencing}
                                onChange={e => {
                                    queryClient.setQueryData(queryKey, () => ({
                                        ...configuration,
                                        enableGeoFencing: e.checked,
                                    }))
                                }}
                            />
                            <label htmlFor="binary">
                                <h5 className="mb-0 text-blue-900 font-semibold font-family-alegreya">
                                    {translateCommons('Enable Geofence ?')}
                                </h5>
                            </label>
                        </div>
                    </div>
                    {/* <div className="flex align-items-center justify-content-between">
                        <div>
                            <h6 className="my-1">
                                {translateCommons(
                                    `This configuration section is used for triggering watches alerting when the patient is out of the geofencing area`,
                                )}
                            </h6>
                        </div>
                      
                    </div> */}

                    <React.Fragment>
                        <BlockUI blocked={!configuration?.enableGeoFencing}>
                            <div
                                className="flex gap-2 mb-5"
                                style={{
                                    overflowX: 'auto',
                                    overflowY: 'hidden',
                                }}>
                                {locationList?.map((item, idx) => (
                                    <div
                                        key={idx}
                                        className={`flex flex-column gap-1 justify-items-center p-2 border-1 border-gray-100 border-round-xl cursor-pointer hover:bg-gray-100 ${
                                            activeIndex.value === idx
                                                ? 'bg-gray-100'
                                                : ''
                                        }`}
                                        onClick={() =>
                                            handleOnActiveIndex(
                                                idx,
                                                [
                                                    item?.latitude,
                                                    item?.longitude,
                                                ],
                                                item.btnType,
                                            )
                                        }>
                                        {' '}
                                        <div
                                            className={`flex justify-content-center py-3 ${
                                                activeIndex.value !== idx
                                                    ? 'hover:text-500'
                                                    : ''
                                            }`}
                                            style={{
                                                color:
                                                    activeIndex.value === idx
                                                        ? 'darkcyan'
                                                        : 'var(--gray-400)',
                                            }}>
                                            {' '}
                                            {item.icon}
                                        </div>
                                        <p
                                            className="font-bold text-base text-700 text-center"
                                            style={{width: 120}}>
                                            {translateCommons(item.label)}
                                        </p>
                                    </div>
                                ))}
                            </div>
                            <div
                                ref={mapContainerRef}
                                className="bg-gray-100 border-round-sm p-2">
                                <BlockUI
                                    blocked={!isMapHovered.value}
                                    id="map-block-ui"
                                    containerStyle={{
                                        backgroundColor: 'transparent',
                                    }}>
                                    <MapContainer
                                        ref={mapRef}
                                        center={center.value}
                                        className="border-round-xl"
                                        zoom={15}
                                        style={{
                                            width: '100%',
                                            height: '70vh',
                                            borderRadius: '5rem',
                                        }}
                                        scrollWheelZoom={true}>
                                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                        {Array.isArray(holderCircle) &&
                                        holderCircle.length !== 2 &&
                                        Array.isArray(circleLocation) &&
                                        circleLocation.length !== 2 ? (
                                            <div></div>
                                        ) : (
                                            <div className="leaflet-top leaflet-right">
                                                <div className="p-inputgroup">
                                                    <span className="p-inputgroup-addon bg-teal-700 text-white">
                                                        {translateCommons(
                                                            'Radius',
                                                        )}
                                                    </span>
                                                    <InputNumber
                                                        className="bg-white leaflet-control"
                                                        style={{
                                                            zIndex: 1000,
                                                            cursor: 'pointer',
                                                            marginTop: 0,
                                                            marginRight: 0,
                                                        }}
                                                        value={radiusValue}
                                                        onChange={e =>
                                                            handleOnChangeValue(
                                                                'radius',
                                                                e.value,
                                                                100,
                                                            )
                                                        }
                                                    />
                                                    <span className="p-inputgroup-addon bg-teal-700 text-white">
                                                        {translateCommons(
                                                            'meters',
                                                        )}
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        {locationList?.map(
                                            (item, idx) =>
                                                !!item?.latitude &&
                                                !!item?.longitude && (
                                                    <Marker
                                                        key={idx}
                                                        position={[
                                                            item.latitude,
                                                            item.longitude,
                                                        ]}
                                                        icon={
                                                            item?.markerIcon
                                                                ? item?.markerIcon()
                                                                : getIconMarker()
                                                        }
                                                        eventHandlers={{
                                                            click: e =>
                                                                handleOnMakerClick(
                                                                    e.latlng,
                                                                ),
                                                        }}></Marker>
                                                ),
                                        )}

                                        {Array.isArray(holderCircle.value) &&
                                            holderCircle.value.length === 2 && (
                                                <Circle
                                                    center={holderCircle.value}
                                                    pathOptions={{
                                                        fillColor: 'gray',
                                                        color: 'gray',
                                                    }}
                                                    radius={radiusValue}
                                                />
                                            )}
                                        {Array.isArray(circleLocation) &&
                                            circleLocation.length === 2 && (
                                                <Circle
                                                    center={circleLocation}
                                                    pathOptions={{
                                                        fillColor: 'darkcyan',
                                                        color: 'darkcyan',
                                                    }}
                                                    radius={radiusValue}
                                                />
                                            )}
                                    </MapContainer>
                                </BlockUI>
                            </div>
                        </BlockUI>
                    </React.Fragment>
                </div>
            )}

            {/* Watch Data Retrieval Config */}
            <div className="flex mb-2 border-round  flex-wrap  w-full justify-content-between align-items-center">
                {/* <h5
                    className={`pl-2 text-black font-bold w-full m-0 py-2 text-center  md:text-left`}>
                    <span className="mr-3">
                        <MdWatch style={{width: '20px', height: '20px'}} />
                    </span>
                    {translateCommons('Watch Configuration')}
                </h5> */}
                <StyledTitle
                    title={translateCommons('Watch Configuration')}
                    icon={<DuoWatch />}
                    description={translateCommons(
                        'This configuration section is used for triggering watches sending data to dashboard',
                    )}
                />
                <AuditLogButton
                    label={translateCommons(
                        "Show Watch Configuration's Audit Log",
                    )}
                    onClick={() => {
                        !!id &&
                            genericRef.current.showForm({
                                refId: id,
                                type: 'watchConfigLog',
                            })
                    }}></AuditLogButton>
            </div>
            <div className="grid px-0 mb-3">
                {watchConfigList.map((item, idx) => (
                    <div key={idx} className="md:col-6 col-12">
                        <h5 className="font-bold">
                            {translateCommons(item.label)}
                        </h5>
                        <div className="p-inputgroup">
                            <span className="p-inputgroup-addon bg-teal-700 text-white">
                                {translateCommons(item?.leftLabel ?? 'Every')}
                            </span>
                            <InputNumber
                                className="col-12 p-0 custom-detail h-3rem"
                                placeholder="Time"
                                min={item.min}
                                max={9999999}
                                value={
                                    configuration?.[item.key] > item.min
                                        ? configuration?.[item.key]
                                        : item.min
                                }
                                onValueChange={e =>
                                    handleOnChangeValue(
                                        item.key,
                                        e.target.value,
                                        item.min,
                                    )
                                }
                            />
                            <span className="p-inputgroup-addon bg-teal-700 text-white">
                                {translateCommons(
                                    `minute(s) - Min: ${
                                        item.min < 10
                                            ? '0' + item.min
                                            : item.min
                                    } minutes`,
                                )}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
            <div>
                {handleCheckPermissionByKey(
                    permissionOptionKeys?.allowToUpdateWatchConfig,
                ) && (
                    <Button
                        className="col-12  p-button-lg font-bold my-5 bg-teal-700 text-white"
                        label={translateCommons('UPDATE CONFIGURATION')}
                        icon={'pi pi-send'}
                        onClick={handleUpdateConfig}></Button>
                )}
            </div>

            {/* Dialog */}
            <GenericLogTable ref={genericRef} />
            <Dialog
                visible={visible.value}
                className="col-12 md:col-6 m-0 p-0"
                onHide={hideDialog}
                header={translateCommons('Address Form')}>
                <p className="m-2">
                    {translateCommons(
                        "This address form is only used for location lookup and does not automatically save the location. Please click 'Update Configuration' button to save the location.",
                    )}
                </p>
                <AddressForm
                    ref={addressRef}
                    className={'grid col-12 m-0 p-0'}
                    childrenClassName={'col-6'}
                    disableList={[
                        'type',
                        'postalCode',
                        'phone',
                        'addressLine2',
                        'addressLine3',
                        'addressLine4',
                    ]}
                />

                <hr />
                <div className="flex w-full justify-content-end">
                    <Button
                        className="mr-2"
                        label={translateCommons('Cancel')}
                        icon="pi pi-times"
                        onClick={hideDialog}
                    />
                    <Button
                        label={translateCommons('Submit')}
                        icon="pi pi-check"
                        onClick={handleGetOtherLocation}
                    />
                </div>
            </Dialog>
        </React.Fragment>
    )
}

export default WatchConfig
